import React, { FC, useState } from 'react';
import { useDeviceHandler } from '~Components/DeviceProvider';
import { useStorage } from '~Components/Storage';
import useModal from '~ComponentsImproved/Modal/useModal';
import { useDispatch, useI18n, useLogger } from '~Hooks';
import HandySettingsBlock from '~Scenes/Handy/Components/HandySettingsBlock';
import DevicesIcon from '~Assets/icons/devices.svg';
import Modal from '~ComponentsImproved/Modal';
import { BlueButton, Box, Button } from '~Styles';
import { ModalWrapper } from '~Components/ClaimDevice/styles';
import Icon from '~ComponentsImproved/Icon';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import styled from '@emotion/styled';
import Loader from '~ComponentsImproved/Loader';
import { PRODUCT_TO_UI_COPY_MAP } from '~Providers/LicensesProvider/Components/ClaimLicense';
import { DateTime } from 'luxon';

const HeaderBox = styled(Box)<{ minHeight?: string }>`
  text-align: center;
  ${({ minHeight }) => minHeight && `min-height: ${minHeight};`}
`;

const ErrorBox = styled(Box)`
  max-width: 480px;
  text-align: center;
`;

/** Select device from the licenses flow */
const SelectDeviceV2: FC = () => {
  const { i18n } = useI18n();
  const dispatch = useDispatch();
  const logger = useLogger('handy-settings');
  const [storage, updateStorage] = useStorage();

  const confirmModal = useModal();

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);

  const isUsingBrowser = !window.ReactNativeWebView;

  const deviceHandler = useDeviceHandler();

  const deviceToUse = deviceHandler.allDevices.find((device) => device.id === selectedDeviceId);

  const handleClaimDevice = async () => {
    setLoading(true);

    logger.info('Handheld: Claiming another device', { selectedDeviceId });

    if (!selectedDeviceId) {
      setLoading(false);
      return;
    }

    if (!deviceToUse) {
      setLoading(false);
      setError('The selected device was not found');
      return;
    }

    try {
      /**
       * If we're logged in browser we can assume it's a Baemingo employee,
       * then we should not claim the device (since it causes a cooldown on the device) but instead
       * just set the same clientId as the device.ownerClientId to simulate usage.
       * This will result in that we use this device (see DeviceProvider)
       */
      if (isUsingBrowser) {
        updateStorage({
          clientId: deviceToUse.ownerClientId,
          deviceId: deviceToUse.id,
        });
      } else {
        const claimDeviceRes = await deviceHandler.claim(selectedDeviceId);

        if (!claimDeviceRes.success) {
          throw new Error('Failed to claim device');
        }
      }

      updateStorage({
        taxComplianceDeviceId: deviceToUse.posDeviceId,
        taxComplianceDeviceName: deviceToUse.name,
        taxComplianceCorporateId: deviceHandler.organizationId,
      });

      confirmModal.hide();
    } catch (error) {
      logger.error(
        'Handheld: (Failed to Claim Device) - ' +
          (typeof error === 'string' ? error : JSON.stringify(error)),
      );
      setError(typeof error === 'string' ? error : i18n('General.Error'));
    }

    setLoading(false);
  };

  const cancelButton = {
    onClick: confirmModal.hide,
  };

  const claimDeviceButton = {
    onClick: handleClaimDevice,
    disabled: loading || !selectedDeviceId,
  };

  const items = deviceHandler.allDevices.map((device) => {
    const isUsedByThisDevice = device?.id === storage.deviceId;

    const hasClaimCooldown = device?.lastClaimedAt
      ? DateTime.fromISO(device.lastClaimedAt, { zone: 'utc' })
          .plus({ days: 7 })
          .toJSDate() > DateTime.utc().toJSDate()
      : false;

    // Create a DateTime object directly from the lastClaimedAt ISO string
    const cooldownEndDate = device?.lastClaimedAt
      ? DateTime.fromISO(device?.lastClaimedAt, { zone: 'utc' })
          .plus({ days: 7 })
          .toFormat('d MMMM HH:mm')
      : null;

    return {
      id: device.id,
      disabled: !isUsingBrowser && (hasClaimCooldown || isUsedByThisDevice),
      disabledToast: hasClaimCooldown
        ? `${i18n('ClaimDevice.Select.DeviceInUse', device?.name)} ${cooldownEndDate}`
        : isUsedByThisDevice
        ? i18n('ClaimDevice.Select.InUseByThisDevice')
        : undefined,
      title: `${device.name} ${
        !device?.ownerClientId
          ? ''
          : `(${
              device?.id === storage.deviceId
                ? i18n('ClaimDevice.Select.InUseByThisDevice')
                : i18n('ClaimDevice.Select.InUse')
            })`
      }`,
      additionalText: {
        left: `${i18n('ClaimDevice.Select.Licenses')}: ${
          device?.activeLicenses.length > 0
            ? [...device?.activeLicenses]
                .sort((a, b) =>
                  (PRODUCT_TO_UI_COPY_MAP[a.product] || '').localeCompare(
                    PRODUCT_TO_UI_COPY_MAP[b.product] || '',
                  ),
                )
                .map(
                  (license, index, sortedArray) =>
                    `${PRODUCT_TO_UI_COPY_MAP[license.product]}${
                      index < sortedArray.length - 1 ? ', ' : ''
                    }`,
                )
                .join('')
            : i18n('ClaimDevice.Select.Licenses.None')
        }`,
      },
      action: {
        onClick() {
          setSelectedDeviceId(device.id);
          confirmModal.toggle();
        },
      },
    };
  });

  return (
    <>
      <HandySettingsBlock items={items} />

      <Modal isShowing={confirmModal.isShowing} hide={confirmModal.hide}>
        <ModalWrapper
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap="xs"
          justifyContent="center"
          padding="small">
          <HeaderBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            marginTop="xs"
            gap="xs"
            justifyContent="center">
            <Icon height="48px" width="48px" src={DevicesIcon} color="var(--colorBlueGray200)" />
            <Heading level={2} color="var(--colorWhite)">
              {i18n('Settings.Device.ConfirmClaim', deviceToUse?.name)}
            </Heading>
            <Paragraph level={4} color="var(--colorBlueGray200)">
              {i18n('Settings.Device.ConfirmClaim.Desc')}
            </Paragraph>
          </HeaderBox>

          <Box fluid display="flex" gap="small" marginTop="medium" marginBottom="xs">
            <Button fluid {...cancelButton}>
              {i18n('General.Cancel')}
            </Button>
            <BlueButton fluid {...claimDeviceButton}>
              {loading ? (
                <Loader size="sm" color="var(--colorWhite)" />
              ) : (
                i18n('ClaimDevice.ClaimDevice')
              )}
            </BlueButton>
          </Box>

          {error && (
            <ErrorBox padding="small" display="flex" flexDirection="column" gap="xs">
              <Paragraph level={4} bottom={0} color="var(--colorUIRed)">
                {error}
              </Paragraph>
            </ErrorBox>
          )}
        </ModalWrapper>
      </Modal>
    </>
  );
};

export default SelectDeviceV2;
